import { Typography, Card, Button, Stack, SvgIconProps } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion } from "framer-motion";
import { ReactNode } from "react";
import { Link } from "react-router";

interface OverviewCardProps {
  title: string;
  icon: React.ComponentType<SvgIconProps>;
  headerContent?: ReactNode;
  children: ReactNode;
  buttonText: string;
  buttonHref?: string;
  onButtonClick?: () => void;
  delay?: number;
  height?: number;
  onClick?: () => void;
}

export const OverviewCard = ({
  title,
  icon: Icon,
  headerContent,
  children,
  buttonText,
  buttonHref,
  onButtonClick,
  delay = 0,
  height = 340,
  onClick,
}: OverviewCardProps) => {
  const button = (
    <Button
      fullWidth
      variant="contained"
      onClick={onButtonClick}
      sx={{
        borderRadius: 0,
      }}
    >
      {buttonText}
      <ArrowForwardIcon sx={{ fontSize: 16 }} />
    </Button>
  );

  const isInteractive = Boolean(onClick ?? buttonHref ?? onButtonClick);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Card
        onClick={onClick}
        elevation={0}
        sx={{
          border: 1,
          borderColor: "common.black",
          borderRadius: 1,
          overflow: "hidden",
          "&:hover": isInteractive
            ? {
                boxShadow: (theme) =>
                  `2px 3px 0px 0px #000,inset 5px 0px 0px 0px ${theme.palette.primary.main}`,
              }
            : undefined,
          transition: "box-shadow 0.2s ease-in-out",
          height: height,
          cursor: onClick ? "pointer" : "default",
        }}
      >
        <Stack sx={{ height: "100%" }}>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ px: 2, height: 60, flexShrink: 0 }}
          >
            <Icon sx={{ color: "primary.main" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            {headerContent}
          </Stack>

          <Stack sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            {children}
          </Stack>

          {buttonHref ? (
            <Link to={buttonHref}>{button}</Link>
          ) : onButtonClick ? (
            button
          ) : null}
        </Stack>
      </Card>
    </motion.div>
  );
};
